import React, { useEffect, useRef } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { toast, ToastContainer } from 'react-toastify';

import dayjs from 'dayjs';

import posthog from 'posthog-js';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { SerializedError } from '@reduxjs/toolkit';

import { useFeatureFlagEnabled } from 'posthog-js/react';

//import { TestEcho, TestMeet } from 'livekit';

import Login from './pages/login';
import Registration from './pages/registration';
import UIKit from './pages/ui-kit';
import Meet from './pages/meet';
import Profile from './pages/profile';
import Loader from './components/common/loader';
import PrivateRouter from './components/private-router';
import { useLazyMeQuery } from './services/users.api';
import { useAppDispatch, useAppSelector } from './hooks/redux';
import { RouteNames } from './configs/routes/routesNames';
import { logout, selectUser, setCredentials, setUserData } from './store/auth.slice';
import Header from './components/header';
import Layout from './components/layout';
import Consultations from './pages/Consultations';
import { EndMeetPage } from './pages/end-call';
import Info from './pages/info';
import Tools from './pages/tools';
import EmotionDetector from './pages/tools/emotion-detector';
import Dpdg from './pages/tools/dpdg';
import Question from './pages/tools/question';
import MetaphoricalCards from './pages/tools/metaphorical-cards';
import Post from './pages/post';
import Tariffs from './pages/tariffs';
import Journal from './pages/journal';
import { JournalConsultationRedirect } from './pages/journal/journal-consultation-redirect';

import ReferalManagement from './pages/refereral-management';
import Subscriptions from './pages/subscriptions';
import { LifeBalanceWheelPage } from './pages/tools/life-balance-wheel-page';
import AiAssistants from './pages/tools/ai-assistants';
import ColorCode from './pages/tools/color-code';
import Clouds from './pages/tools/clouds';
import AiGrow from './pages/tools/ai-grow';
import SandTherapy from './pages/tools/sand-therapy';
import Arrangements from './pages/tools/arrangements';

import { useTariffDataUpload } from './hooks/useTariffDataUpload';
import AppSettings from './components/settings/app-settings';
import { FEATURE_FLAG_PLANS_V2 } from './constants/featureFlags';
dayjs.locale('ru');

export const tokensLocal = JSON.parse(localStorage.getItem('tokensZZ') || '{}');

function getRtkQueryErrorStatus(error: FetchBaseQueryError | SerializedError): number | undefined {
  const status = (error as FetchBaseQueryError).status;
  return typeof status === 'number' ? status : undefined;
}

function App() {
  const [me, { data, error }] = useLazyMeQuery();
  const isPlansV2Enabled = useFeatureFlagEnabled(FEATURE_FLAG_PLANS_V2);
  const userData = useAppSelector(selectUser);
  const { isDone: isTariffsLoadDone } = useTariffDataUpload(userData !== null);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const identifiedPsychologistRef = useRef<number | null>(null);

  const [isInitialized, setIsInitialized] = React.useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    if (JSON.parse(localStorage.getItem('tokensZZ') || '{}')) {
      if (Object.keys(JSON.parse(localStorage.getItem('tokensZZ') || '{}')).length === 0) return;
      dispatch(setCredentials(tokensLocal));
      me();
    } else {
      navigate(RouteNames.AUTHORIZATION);
    }

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (error) {
      const status = getRtkQueryErrorStatus(error);
      if (status === 422) {
        toast.error(
          'Ваш аккаунт заблокирован, чтобы разблокировать, обратитесь к администратору сервиса info@zingizi.pro',
        );
      }
      if (status === 401) {
        toast.error('Сессия истекла. Пожалуйста, войдите снова.');
      }
      dispatch(logout());
      navigate(RouteNames.AUTHORIZATION);
    }
    // eslint-disable-next-line
  }, [error]);

  useEffect(() => {
    if (data) {
      dispatch(setUserData(data));
    }
    // eslint-disable-next-line
  }, [data]);

  useEffect(() => {
    if (userData && isTariffsLoadDone) {
      setIsInitialized(true);
    }
  }, [userData, isTariffsLoadDone]);

  useEffect(() => {
    if (!userData?.id) {
      if (identifiedPsychologistRef.current !== null) {
        posthog.reset();
        identifiedPsychologistRef.current = null;
      }

      return;
    }

    if (identifiedPsychologistRef.current === userData.id) {
      return;
    }

    const distinctId = `psychologist_${userData.id}`;
    const fullName = [userData.last_name, userData.first_name, userData.middle_name].filter(Boolean).join(' ').trim();

    posthog.identify(distinctId, {
      role: 'psychologist',
      user_id: userData.id,
      email: userData.email,
      name: fullName || undefined,
    });
    posthog.group('user_role', 'psychologist', { role: 'psychologist' });

    identifiedPsychologistRef.current = userData.id;
  }, [userData?.email, userData?.first_name, userData?.id, userData?.last_name, userData?.middle_name]);

  if (!isInitialized && JSON.parse(localStorage.getItem('tokensZZ') || '{}')?.token) {
    return <Loader />;
  }

  return (
    <>
      <AppSettings>
        <Routes>
          <Route
            element={
              <>
                <Header />
                <Outlet />
              </>
            }
          >
            <Route index path="/" element={<Login />} />
            <Route path="/registration" element={<Registration />} />
          </Route>
          <Route path="/meet/:id" element={<Meet />} />
          <Route index path="/end-call/:id/:success?" element={<EndMeetPage />} />
          <Route element={<PrivateRouter />}>
            <Route element={<Layout />}>
              <Route index path="/profile" element={<Profile />} />
              <Route index path="/consultations" element={<Consultations />} />
              <Route path="/journal" element={<Journal />} />
              <Route path="/journal/:id" element={<JournalConsultationRedirect />} />

              <Route path="/tools" element={<Tools />}>
                <Route path="emotion-detector" element={<EmotionDetector />} />
                <Route path="metaphorical-cards" element={<MetaphoricalCards />} />
                <Route path="question" element={<Question />} />
                <Route path="dpdg" element={<Dpdg />} />
                <Route path="life-balance-wheel" element={<LifeBalanceWheelPage />} />
                <Route path="ai-assistants" element={<AiAssistants />} />
                <Route path="color-code" element={<ColorCode />} />
                <Route path="clouds" element={<Clouds />} />
                <Route path="ai-grow" element={<AiGrow />} />
                <Route path="sand-therapy" element={<SandTherapy />} />
                <Route path="arrangements" element={<Arrangements />} />
              </Route>
              <Route
                index
                path="/tariffs"
                element={isPlansV2Enabled ? <Navigate to="/subscriptions" replace /> : <Tariffs />}
              />
              <Route index path="/subscriptions" element={<Subscriptions />} />
              <Route path="/info" element={<Info />}>
                <Route path=":id" element={<Post />} />
              </Route>
              <Route path="/referal-management" element={<ReferalManagement />} />
            </Route>
            <Route path="/ui-kit" element={<UIKit />} />
          </Route>
          <Route path={'*'} element={<div className="flex py-[100px]">Упс, такой страницы не существует</div>} />
        </Routes>
        <ToastContainer style={{ zIndex: 9999999999999 }} />
      </AppSettings>
    </>
  );
}

export default App;
